/**
 * A user agent string, reduced to something a person can recognise.
 *
 * The sessions list exists so someone can spot a device that is not theirs,
 * and a raw user agent defeats that: on a phone it is four lines of version
 * numbers, and the part that matters is buried. Order matters in both tables
 * below, because these strings lie -- every Chromium browser claims to be
 * Safari, and Edge claims to be Chrome -- so the most specific claim has to be
 * tested first.
 *
 * The full string is still shown, as the element's title, because a summary is
 * a convenience and the original is the evidence.
 */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg[A-Z]?\//, "Edge"],
  [/\bOPR\//, "Opera"],
  [/\bSamsungBrowser\//, "Samsung Internet"],
  [/\bFirefox\//, "Firefox"],
  [/\bChrome\//, "Chrome"],
  [/\bSafari\//, "Safari"],
];

const SYSTEMS: [RegExp, string][] = [
  [/\bAndroid\b/, "Android"],
  [/\b(iPhone|iPad|iPod)\b/, "iOS"],
  [/\bWindows NT\b/, "Windows"],
  [/\b(Macintosh|Mac OS X)\b/, "macOS"],
  [/\bCrOS\b/, "ChromeOS"],
  [/\bLinux\b/, "Linux"],
];

function firstMatch(table: [RegExp, string][], agent: string): string | null {
  return table.find(([pattern]) => pattern.test(agent))?.[1] ?? null;
}

export function describeDevice(userAgent: string | null): string {
  if (userAgent === null || userAgent.trim().length === 0) return "Unknown device";

  const browser = firstMatch(BROWSERS, userAgent);
  const system = firstMatch(SYSTEMS, userAgent);

  if (browser !== null && system !== null) return `${browser} on ${system}`;
  if (browser !== null) return browser;
  if (system !== null) return system;

  // Something unrecognised: a script, a native client, a browser newer than
  // this table. Show what it said rather than calling it unknown, bounded so
  // one long string cannot dominate the list.
  const raw = userAgent.trim();
  return raw.length > 60 ? `${raw.slice(0, 60)}…` : raw;
}
